import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Menu, MenuItem, IconButton, Radio, Divider, InputAdornment, CircularProgress } from '@mui/material';
import { connect, useDispatch } from 'react-redux';
import { appendUserListUsers, updateUserLists } from '../../../redux/actions/userLists';
import User from './User';
import { Virtuoso, VirtuosoGrid, VirtuosoGridHandle } from 'react-virtuoso';
import { useTheme } from '@mui/material/styles';
import useDebounce from '../../utils/useDebounceV2';
import useDidMountEffect from '../../utils/useDidMountEffect';

const DEBOUNCE_DELAY = 800;

const AddUsers = ({
    show,
    setShow,
    activeUserListRdx
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();

    const [followers, setFollowers] = useState([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [selectedFollowers, setSelectedFollowers] = useState([]);
    const [followersBusy, setFollowersBusy] = useState(false);
    const [selectedIDs, setSelectedIDs] = useState([]);
    const [keyword, setKeyword] = useState('');
    const [showListMenu, setShowListMenu] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [usersSort, setUsersSort] = useState('users.login');
    const [order, setOrder] = useState('desc');

    const getFollowers = async () => {
        setFollowersBusy(true);
        await axios.get('/api/user-lists/followers', {
            params: {
                list_id: activeUserListRdx.id,
                kw: keyword,
                sort: usersSort,
                order: order
            }
        })
            .then(response => {
                setFollowers(response.data);
                setFollowersBusy(false);
            })
            .catch(error => {
                setFollowersBusy(false);
            });
    };

    const handleClose = () => {
        setShow(false);
    };

    const handleCreate = () => {
        setBusy(true);
        axios.post('/api/user-list-users', {
            list_id: activeUserListRdx.id,
            user_ids: selectedFollowers.map(sf => sf.id)
        })
            .then(response => {
                setBusy(false);
                dispatch(appendUserListUsers(response.data.data));
                dispatch(updateUserLists(response.data.user_list));
                setShow(false);
            })
            .catch(error => {
                setBusy(false);
                if (error.response) {
                    if (error.response.status === 422) {
                        const responseData = error.response.data;
                        const firstErrorKey = Object.keys(responseData.errors);
                        setError(responseData.errors[firstErrorKey[0]]);
                    }
                }
            });
    };

    const handleFollowerChange = (follower) => {
        let tmp = [...selectedFollowers];
        const index = tmp.findIndex(f => f.id == follower.id);

        if (index >= 0) {
            tmp.splice(index, 1);
        } else {
            tmp.push(follower);
        }

        setSelectedFollowers([...tmp]);
    };

    useEffect(() => {
        if (selectedFollowers.length > 0) {
            setSelectedIDs([...selectedFollowers.map(follower => follower.id)]);
        } else {
            setSelectedIDs([]);
        }

        return () => { };
    }, [selectedFollowers]);

    useEffect(() => {
        getFollowers();

        return () => {
            setSelectedFollowers([]);
        };
    }, []);

    const handleKeywordChange = e => {
        const value = e.target.value;
        setKeyword(value);
    };

    const handleUsersEnd = () => { };
    const handleShowDeleteUserConfirm = () => { };

    const handleClearKeyword = () => {
        setKeyword('');
    };

    const handleOpenUserListOption = e => {
        setAnchorEl(e.currentTarget);
        setShowListMenu(true);
    };

    const handleSortChange = e => {
        setUsersSort(e.target.value);
    };

    const handleUserOrderChange = e => {
        setOrder(e.target.value);
    };

    const debounceUsersSearch = useDebounce(() => {
        getFollowers();
    }, [DEBOUNCE_DELAY]);

    useDidMountEffect(() => {
        debounceUsersSearch();
    }, [keyword]);

    useDidMountEffect(() => {
        getFollowers();
    }, [usersSort, order]);

    return <Dialog
        className='user-list-form'
        open={show}
        onClose={handleClose}
        sx={{
            '& .MuiDialog-paper': {
                maxWidth: '480px',
                width: '480px',
                minHeight: '70vh'
            }
        }}
    >
        <DialogTitle
            sx={{
                color: '#0097fe'
            }}
        >Add users to {activeUserListRdx.name}</DialogTitle>
        <DialogContent>
            <div className="content__users">
                <div
                    className="content__search"
                >
                    <div>
                        <TextField
                            id="keyword"
                            label="Search"
                            value={keyword}
                            onChange={handleKeywordChange}
                            size='small'
                            InputProps={{
                                endAdornment: (
                                    !!keyword
                                        ? <IconButton
                                            aria-label="clear text"
                                            onClick={handleClearKeyword}
                                            edge="end"
                                        >
                                            <i style={{ color: '#0097fe' }} className="fal fa-times search-clear-icon"></i>
                                        </IconButton>
                                        : ''
                                ),
                                startAdornment: <InputAdornment position="start">
                                    <i className="fa-regular fa-magnifying-glass"></i>
                                </InputAdornment>,
                            }}
                        />
                    </div>
                    <div>
                        <IconButton
                            aria-label="New User List"
                            onClick={handleOpenUserListOption}
                            aria-controls={showListMenu ? 'basic-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={showListMenu ? 'true' : undefined}
                        >
                            <i className="fa-solid fa-bars-sort icon"></i>
                        </IconButton>
                        <Menu
                            anchorEl={anchorEl}
                            id="basic-menu"
                            open={showListMenu}
                            onClose={() => setShowListMenu(false)}
                            MenuListProps={{
                                'aria-labelledby': 'basic-button',
                            }}
                            sx={{
                                '&.MuiPopover-root': {
                                    zIndex: 10001
                                }
                            }}
                        >
                            <MenuItem onClick={() => { }}>
                                <Radio
                                    value='followers.subscribed_at'
                                    checked={usersSort === 'followers.subscribed_at'}
                                    name="radio-buttons"
                                    inputProps={{ 'aria-label': 'followers.subscribed_at' }}
                                    onChange={handleSortChange}
                                /> Recent
                            </MenuItem>
                            <MenuItem onClick={() => { }}>
                                <Radio
                                    value='users.login'
                                    checked={usersSort === 'users.login'}
                                    name="radio-buttons"
                                    inputProps={{ 'aria-label': 'name' }}
                                    onChange={handleSortChange}
                                /> Name
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={() => { }}>
                                <Radio
                                    value='asc'
                                    checked={order === 'asc'}
                                    name="radio-buttons"
                                    inputProps={{ 'aria-label': 'asc' }}
                                    onChange={handleUserOrderChange}
                                /> Ascending
                            </MenuItem>
                            <MenuItem onClick={() => { }}>
                                <Radio
                                    value='desc'
                                    checked={order === 'desc'}
                                    name="radio-buttons"
                                    inputProps={{ 'aria-label': 'desc' }}
                                    onChange={handleUserOrderChange}
                                /> Descending
                            </MenuItem>
                        </Menu>
                    </div>

                    <div className='content__count'>
                        {selectedIDs.length} selected
                    </div>
                </div>
                {
                    followers.length > 0
                        ? <VirtuosoGrid
                            style={{
                                height: '60vh'
                            }}
                            totalCount={followers.length}
                            endReached={handleUsersEnd}
                            itemClassName='user__card'
                            data={followers}
                            itemContent={(index, user) => <React.Fragment>
                                <User
                                    key={user.id}
                                    user={user}
                                    onDelete={handleShowDeleteUserConfirm}
                                    active={{}}
                                    selectable
                                    onSelect={handleFollowerChange}
                                    isSelected={selectedIDs.includes(user.id)}
                                />
                            </React.Fragment>}
                        />
                        : <div className='no__result'>
                            {
                                !followersBusy
                                    ? 'No users found'
                                    : <div style={{ display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></div>
                            }
                        </div>
                }
            </div>
        </DialogContent>
        <DialogActions>
            <Button
                onClick={handleClose}
                disabled={busy || followersBusy}
            >Cancel</Button>
            <Button
                onClick={handleCreate}
                disabled={busy || followersBusy}
            >
                {busy ? 'Saving...' : 'Save'}
            </Button>
        </DialogActions>
    </Dialog>;
};

const mapStateToProps = state => ({
    activeUserListRdx: state.userLists.activeUserList,
});

export default connect(mapStateToProps)(AddUsers);